import type { DatumEngine } from '@datum-sdk/engine';
import type { CameraViewOffsetConfig } from '../../cameraConfig';
import { cubicBezierEasing } from '../camera/PrincipalPointController';

type Edges = { top: number; right: number; bottom: number; left: number };

const EDGES = ['top', 'right', 'bottom', 'left'] as const;

/** The panel's default transition — PrincipalPointController parity. */
const DEFAULT_DURATION_MS = 350;

/** Below this every edge is already in place — land instead of tweening. */
const SETTLED_PX = 0.5;

const readEdges = (viewOffset: CameraViewOffsetConfig, out: Edges): void => {
  for (const edge of EDGES) out[edge] = viewOffset.enabled ? Math.max(0, viewOffset[edge]) : 0;
};

/**
 * Chat-panel reframe on the SDK path. `viewOffset` is the canvas area the chat
 * UI covers, in px per physical edge; `setViewportPadding` frames the scene into
 * the rest with a lens shift — the same centre-of-visible-rect the legacy
 * PrincipalPointController computes, without patching the SDK camera. The engine
 * applies a padding change at once, so the panel's own CSS transition is
 * mirrored by tweening the edges (the shift is linear in them) on the same curve
 * and duration. A change mid-tween retargets from where the tween is;
 * `animate: false` (a drag, the first apply) lands at once.
 */
export const createViewportPaddingTween = (engine: DatumEngine) => {
  // One object per role, reused every frame — the engine copies what it is given.
  const current: Edges = { top: 0, right: 0, bottom: 0, left: 0 };
  const from: Edges = { top: 0, right: 0, bottom: 0, left: 0 };
  const to: Edges = { top: 0, right: 0, bottom: 0, left: 0 };
  let startMs = 0;
  let durationMs = DEFAULT_DURATION_MS;
  let raf = 0;

  const edgeIsZero = (edge: keyof Edges): boolean => current[edge] === 0;
  const write = (): void => {
    engine.setViewportPadding(EDGES.every(edgeIsZero) ? undefined : current);
  };

  const step = (now: number): void => {
    const progress = Math.min(1, (now - startMs) / durationMs);
    const eased = cubicBezierEasing(progress);
    for (const edge of EDGES) current[edge] = from[edge] + (to[edge] - from[edge]) * eased;
    write();
    raf = progress < 1 ? requestAnimationFrame(step) : 0;
  };

  return {
    apply: (viewOffset: CameraViewOffsetConfig, animate: boolean): void => {
      readEdges(viewOffset, to);
      cancelAnimationFrame(raf);
      raf = 0;
      const settled = EDGES.every((edge) => Math.abs(to[edge] - current[edge]) < SETTLED_PX);
      if (!animate || settled) {
        // The engine already holds `current`, and every padding write redraws the splats.
        if (EDGES.every((edge) => to[edge] === current[edge])) return;
        Object.assign(current, to);
        write();
        return;
      }
      Object.assign(from, current);
      startMs = performance.now();
      // 0 means "the default", as it did for PrincipalPointController.
      durationMs = viewOffset.animationDurationMs || DEFAULT_DURATION_MS;
      raf = requestAnimationFrame(step);
    },
    dispose: (): void => {
      cancelAnimationFrame(raf);
      raf = 0;
    },
  };
};
